/**
 * Site-works matrices (Roads & Sewers, non-drainage sections)
 * ===========================================================
 *
 * `runMatrixGenerator.ts` builds the drainage Run Matrix — one row per pipe run.
 * The rest of a Roads & Sewers tender has no pipe runs, but each section does
 * have its own natural unit of measure, so each gets its own matrix here:
 *
 * | Section          | Rows (unit)                             | Columns                          |
 * |------------------|-----------------------------------------|----------------------------------|
 * | Preliminaries    | each bill group                         | charge type (fixed / weekly / …) |
 * | Roads            | each road                               | Carriageway Summary sections     |
 * | Main Services    | each road                               | utility (Water, Gas, BT, …)      |
 * | Substation       | Sub Station Base / Access               | work category                    |
 * | POS              | each Public Open Space                  | ground prep / gravel / macadam / LEAP |
 * | Compound         | compound, silo base, carpark, …         | work category                    |
 * | Spoil            | each area of the scheme                 | topsoil & subsoil volumes (m³)   |
 * | Foul build-ups   | pump-station elements, rising main      | work category                    |
 * | Storm build-ups  | each pond, swale, headwall, tank, culvert | work category                  |
 *
 * The last two are not top-level sections — they render as a sub-tab under
 * Drainage Runs → Foul Sewers / Storm Sewers, mirroring how the Foul and Storm
 * Bill Summary tabs nest their own build-up tabs.
 *
 * Same contract as the drainage matrix: nothing is re-calculated. Every builder
 * reads the same row definitions, quantities and evaluate-code lookups the
 * section's own Bill Summary tab renders, prices them with the same item-library
 * map, and reconciles against that section's own total function
 * (`sumRoadsBillSummaryAmount`, `sumMainServicesAmount`, `sumSubstationAmount`,
 * `sumPosAmount`, `sumCompoundAmount`) — exposed as `billTotal` / `variance` so
 * the UI can prove the tie-out instead of asserting it.
 *
 * Where a section's items have no second axis of their own (Substation,
 * Compound), the columns are **work categories** classified from each item's
 * description by `classifyWorkCategory`. That is a keyword match, so it can put
 * an item in a neighbouring column — but never loses one: unmatched items land in
 * "Sundries", and every cell can be opened to see exactly which items it holds.
 * Row and section totals are unaffected by how an item is classified.
 *
 * Sections with no evaluate codes in the item library price to £0 — Compound has
 * none assigned in the source spec at all today, and many Roads rows have none
 * yet. `pricedItems` / `measuredItems` on each matrix make that visible rather
 * than leaving the estimator to wonder why a column reads zero.
 */

import type {
  CompoundConfig,
  FoulBuildUpRowData,
  PrelimsItemRecord,
  PumpChamberConfig,
  RoadsData,
} from '../types/tender.types';
import {
  forEachPrelimsItem,
  sumPrelimsAmount,
} from './prelimsMapping';
import { PUMP_STATION_ROWS, RISING_MAIN_ROWS, applyPumpStationSectionTitles, applyPumpStationLadderType } from './foulBuildUpRows';
import { withDashedTitleSuffix } from './stormBuildUpTitles';
import type { PumpChamberConfigForEvaluateCode } from './foulBuildUpEvaluateCodesMapping';
import { buildBuildUpSummaryData } from './foulSummaryUtils';
import { buildStormBuildUpSummaryData } from './stormSummaryUtils';
import type { StormBuildUpRowDef } from './stormBuildUpTypes';
import { getPondsRows, DEFAULT_POND_SECTIONS } from './stormBuildUpPonds';
import { getSwalesRows, DEFAULT_SWALE_SECTIONS } from './stormBuildUpSwales';
import { HEADWALL_ROWS } from './stormBuildUpHeadwalls';
import {
  getCellularAttenuationRows,
  DEFAULT_CELLULAR_SECTIONS,
} from './stormBuildUpCellularAttenuation';
import {
  getWeholiteAttenuationRows,
  DEFAULT_WEHOLITE_SECTIONS,
} from './stormBuildUpWeholiteAttenuation';
import { getConcAttenuationRows, DEFAULT_CONC_SECTIONS } from './stormBuildUpConcAttenuation';
import { getBoxCulvertsRows, DEFAULT_BOX_CULVERT_SECTIONS } from './stormBuildUpBoxCulverts';
import {
  findPondsSwalesEvaluateCode,
  findHeadwallEvaluateCode,
} from './stormPondsSwalesEvaluateCodesMapping';
import { findCellularAttenuationEvaluateCode } from './stormCellularAttenuationEvaluateCodesMapping';
import { findWeholiteAttenuationEvaluateCode } from './stormWeholiteAttenuationEvaluateCodesMapping';
import { resolveConcAttenuationEvaluateCode } from './stormConcAttenuationEvaluateCodesMapping';
import { findBoxCulvertsEvaluateCode } from './stormBoxCulvertsEvaluateCodesMapping';
import {
  buildRoadsBillSummarySections,
  sumRoadsBillSummaryAmount,
} from './roadsSummaryCalculations';
import {
  MAIN_SERVICE_TRENCHING_MAPPING,
  sumMainServicesAmount,
} from './mainServiceTrenchingMapping';
import { findMainServiceTrenchingEvaluateCode } from './mainServicesEvaluateCodesMapping';
import { SUBSTATION_BASE_ROW_DEFS, sumSubstationAmount } from './substationBaseRowDefs';
import { findSubstationBaseEvaluateCode } from './substationBaseEvaluateCodesMapping';
import {
  DEFAULT_POS_SECTIONS,
  getPOSConfigurableField,
  getPOSRowDefs,
  sumPosAmount,
  isPosGeotextileExcluded,
} from './posRowDefs';
import { findPosEvaluateCode } from './posEvaluateCodesMapping';
import {
  COMPOUND_ROW_DEFS,
  getCompoundLabels,
  sumCompoundAmount,
} from './compoundRowDefs';
import { findCompoundEvaluateCode } from './compoundEvaluateCodesMapping';
import {
  buildSpoilReconciliationSummary,
  type SpoilReconciliationSource,
} from './spoilReconciliationSummary';
import { markedUpRate } from './runMatrixGenerator';

// ── Shared types ─────────────────────────────────────────────────────────────

export type SiteWorksSectionKey =
  | 'prelims'
  | 'roads'
  | 'main-services'
  | 'substation'
  | 'pos'
  | 'compound'
  | 'spoil'
  // Not top-level: these two render as sub-tabs under Drainage Runs → Foul / Storm.
  | 'foul-build-ups'
  | 'storm-build-ups';

export type MatrixValueKind = 'currency' | 'volume';

export interface SectionMatrixColumn {
  key: string;
  group: string;
  label: string;
  description: string;
}

/** One measured item (or one derived volume) behind a cell. */
export interface SectionMatrixCellLine {
  description: string;
  qty: number;
  unit: string;
  rate?: number;
  amount: number;
  source: string;
  evaluateCode?: string;
}

export interface SectionMatrixCell {
  total: number;
  lines: SectionMatrixCellLine[];
  /** Volume matrices leave a cell blank rather than showing 0 when unmeasured. */
  notMeasured?: boolean;
}

export interface SectionMatrixRow {
  key: string;
  /** `band` = a full-width group heading, `unit` = a measurable row. */
  rowKind: 'unit' | 'band';
  label: string;
  /** Small grey line under the label — road count, source of the figures, etc. */
  sublabel?: string;
  cells: Record<string, SectionMatrixCell>;
  total: number;
  /** Items with a quantity against this row, priced or not. */
  measuredCount: number;
}

export interface SectionMatrix {
  key: SiteWorksSectionKey;
  title: string;
  /** Header of the fixed first column ("Road", "Element", "Area", …). */
  unitHeader: string;
  valueKind: MatrixValueKind;
  columns: SectionMatrixColumn[];
  rows: SectionMatrixRow[];
  columnTotals: Record<string, number>;
  grandTotal: number;
  /** The section's own Bill Summary total, computed independently. */
  billTotal: number;
  /** billTotal − grandTotal. Zero unless something is wrong. */
  variance: number;
  /** Rows with a quantity entered. */
  measuredItems: number;
  /** Of those, how many resolved to an item-library rate. */
  pricedItems: number;
  /** Header of the row-total column; volume matrices call it something else. */
  totalHeader: string;
  /** Shown under the table. */
  note: string;
  /** Shown instead of the table when there are no rows. */
  emptyHint: string;
}

// ── Work-category classification (Substation, Compound) ──────────────────────

export const WORK_CATEGORY_COLUMNS: readonly SectionMatrixColumn[] = [
  {
    key: 'earthworks',
    group: 'Earthworks',
    label: 'Excavate & Dispose',
    description:
      'Excavation, breaking out, level & compact, backfill, filling and carting away.',
  },
  {
    key: 'subbase',
    group: 'Build-up',
    label: 'Sub-base & Capping',
    description: '6F2 / Type 1 / capping layers, membranes and geotextiles.',
  },
  {
    key: 'concrete',
    group: 'Build-up',
    label: 'Concrete & Formwork',
    description: 'Concrete, blinding, mesh and steel reinforcement, formwork and blockwork.',
  },
  {
    key: 'surfacing',
    group: 'Build-up',
    label: 'Surfacing',
    description:
      'Base / binder / surface courses, block paving, paving slabs, gravel surfacing, tack coats and chippings.',
  },
  {
    key: 'edgings',
    group: 'Build-up',
    label: 'Kerbs & Edgings',
    description: 'Kerbs and edgings of every type.',
  },
  {
    key: 'services',
    group: 'Services',
    label: 'Drainage & Services',
    description: 'Pipes, ducts, gullies, chambers, service runs, earthing and joint boxes.',
  },
  {
    key: 'landscape',
    group: 'Finishes',
    label: 'Landscaping',
    description: 'Topsoil, seeding, turf, planting and vegetation clearance.',
  },
  {
    key: 'furniture',
    group: 'Finishes',
    label: 'Furniture & Signage',
    description: 'Fencing, barriers, bollards, gates, lighting columns, signage and lining.',
  },
  {
    key: 'sundries',
    group: 'Finishes',
    label: 'Sundries',
    description:
      'Anything the categories above do not match — setting out, testing, sundry allowances.',
  },
] as const;

/**
 * Keyword classification of a build-up item into a work category. Order matters:
 * the more specific patterns are tested first (e.g. "Spread and level topsoil" is
 * landscaping, not earthworks, even though it contains "level").
 */
const WORK_CATEGORY_PATTERNS: ReadonlyArray<{ key: string; test: RegExp }> = [
  { key: 'landscape', test: /topsoil|top soil|seeding|turf|planting|shrub|vegetation|landscap|grass/i },
  { key: 'edgings', test: /kerb|edging/i },
  {
    key: 'surfacing',
    test: /base course|binder|bindercourse|surface course|surfac|tarmac|macadam|\bac\s?\d+|dbm|block ?pav|paving|gravel|sealing grit|tack coat|\btack\b|sweep|chipping|psv/i,
  },
  {
    key: 'concrete',
    test: /concrete|\brc\s?\d+|mesh|formwork|forwork|blockwork|blinding|chamfer|rebate|reinforc|\bsteel\b|dowel|\bpads?\b/i,
  },
  {
    key: 'subbase',
    test: /6f2|6f5|type ?1|sub ?-? ?base|capping|terram|geotextile|membrane|ogcr|mot type|no ?dig|hardcore/i,
  },
  {
    key: 'services',
    test: /pipe|duct|gully|gulley|ppic|chamber|manhole|\bservice|drain|sleeve|earthing|welding|joint box|\bbox\b/i,
  },
  {
    key: 'earthworks',
    test: /excavat|breakout|break out|\bcart\b|dispos|reduced level|reduce level|backfill|\bfill/i,
  },
  {
    key: 'furniture',
    test: /fence|barrier|armco|bollard|\bgate\b|\bsign|lining|post|\brail|nameplate|\bbin\b|bench|flagpole|equipment|column|lighting|bund/i,
  },
  // Generic ground-prep wording, checked after the specific layers above so
  // "Level and compact formation" lands in Earthworks but "Level and lay 6F2"
  // still reads as sub-base.
  { key: 'earthworks', test: /level (and|&) compact|formation|strip|grub|remove/i },
];

export const classifyWorkCategory = (description: string): string => {
  const match = WORK_CATEGORY_PATTERNS.find((p) => p.test.test(description));
  return match?.key ?? 'sundries';
};

// ── Internal helpers ─────────────────────────────────────────────────────────

interface MatrixBuilderState {
  columns: SectionMatrixColumn[];
  rows: SectionMatrixRow[];
  measuredItems: number;
  pricedItems: number;
}

const emptyCells = (columns: readonly SectionMatrixColumn[]): Record<string, SectionMatrixCell> => {
  const cells: Record<string, SectionMatrixCell> = {};
  columns.forEach((col) => {
    cells[col.key] = { total: 0, lines: [] };
  });
  return cells;
};

const makeRow = (
  key: string,
  label: string,
  columns: readonly SectionMatrixColumn[],
  extras: Partial<SectionMatrixRow> = {},
): SectionMatrixRow => ({
  key,
  rowKind: 'unit',
  label,
  cells: emptyCells(columns),
  total: 0,
  measuredCount: 0,
  ...extras,
});

/**
 * Adds one measured item to a cell. Items with no item-library rate are still
 * recorded (amount 0, no rate) so the estimator can open the cell and see the
 * measured quantity — several of these sections have few or no evaluate codes
 * assigned yet, and a silently empty cell would read as "nothing measured".
 */
const pushLine = (
  row: SectionMatrixRow,
  columnKey: string,
  line: SectionMatrixCellLine,
): void => {
  const cell = row.cells[columnKey];
  if (!cell) return;
  cell.total += line.amount;
  cell.lines.push(line);
  row.total += line.amount;
  row.measuredCount += 1;
};

const finalise = (
  key: SiteWorksSectionKey,
  title: string,
  unitHeader: string,
  state: MatrixBuilderState,
  billTotal: number,
  meta: {
    valueKind?: MatrixValueKind;
    totalHeader?: string;
    note: string;
    emptyHint: string;
  },
): SectionMatrix => {
  const columnTotals: Record<string, number> = {};
  state.columns.forEach((col) => {
    columnTotals[col.key] = state.rows
      .filter((row) => row.rowKind === 'unit')
      .reduce((sum, row) => sum + (row.cells[col.key]?.total ?? 0), 0);
  });
  const grandTotal = state.rows
    .filter((row) => row.rowKind === 'unit')
    .reduce((sum, row) => sum + row.total, 0);

  return {
    key,
    title,
    unitHeader,
    valueKind: meta.valueKind ?? 'currency',
    columns: state.columns,
    rows: state.rows,
    columnTotals,
    grandTotal,
    billTotal,
    variance: billTotal - grandTotal,
    measuredItems: state.measuredItems,
    pricedItems: state.pricedItems,
    totalHeader: meta.totalHeader ?? 'Total (£)',
    note: meta.note,
    emptyHint: meta.emptyHint,
  };
};

/** `resourceCode → nettRate`, already marked up. */
export interface MatrixPricing {
  rateMap: Record<string, number>;
  markupFactor: number;
}

// Exact match only (trimmed, never case-normalized) — deliberately mirrors
// the Bill Summaries whose own rate lookup is exact-only (e.g.
// `FoulBuildUpBillSummary.tsx`'s `getLibRate`, `StormBuildUpBillSummary.tsx`'s
// `getLibRate`, Substation/POS/Compound's own `rateMap`), so a row this helper
// can't price is a row those bills can't price either — not something this
// matrix should paper over with a fuzzier match than the bill itself uses, or
// its total stops tying to the bill's.
//
// Roads, Main Services and Preliminaries are the sections whose own bill
// *does* normalize (`RoadsBillSummary.tsx`'s and `MainServicesBillSummary.tsx`'s
// `resourceCodeMap`/`rateMap`, `PrelimsBillSummary.tsx`'s `resourceCodeMap`) —
// `buildRoadsMatrix`/`buildMainServicesMatrix`/`buildPrelimsMatrix` each build
// their own normalized lookup for that reason (see `buildNormalizedRateMap`
// below) instead of asking this shared helper to normalize for everyone.
//
// A "+"-joined code (e.g. Storm's "ALPE010+L001B", Pump Station's "50mm
// Gravel & Geotextile Areas" → "ALP050+ALP031") is split into its parts and
// their rates summed — every Bill Summary that can produce a combined code
// (`FoulBuildUpBillSummary.tsx`'s and `StormBuildUpBillSummary.tsx`'s own
// `getLibRate`, `useRoadsAndSewersTotalSync.ts`'s `getFoulLibRate`/
// `getStormLibRate`) does the same, so this always matches every one of them.
const baseRateFor = (rateMap: Record<string, number>, code: string): number | undefined => {
  if (code.includes('+')) {
    const parts = code
      .split('+')
      .map((part) => rateMap[part.trim()])
      .filter((rate): rate is number => rate !== undefined);
    return parts.length > 0 ? parts.reduce((sum, rate) => sum + rate, 0) : undefined;
  }
  return rateMap[code.trim()];
};

const rateFor = (pricing: MatrixPricing, code: string | undefined): number | undefined => {
  if (!code) return undefined;
  const base = baseRateFor(pricing.rateMap, code);
  if (base == null) return undefined;
  return markedUpRate(base, pricing.markupFactor);
};

/** Trim+uppercase copy of a raw `MatrixPricing.rateMap` — for the sections whose own Bill
 * Summary normalizes both sides of its item-library lookup (Roads, Main Services,
 * Preliminaries; see the comment on `baseRateFor` above). Last-wins on a collision, same as
 * `resourceCodeMap`'s `Map.set` in those Bill Summaries — a later duplicate resourceCode
 * differing only by case/whitespace overwrites rather than being ignored. */
const buildNormalizedRateMap = (rateMap: Record<string, number>): Record<string, number> => {
  const normalized: Record<string, number> = {};
  Object.entries(rateMap).forEach(([code, rate]) => {
    normalized[code.trim().toUpperCase()] = rate;
  });
  return normalized;
};

const normalizedBaseRateFor = (normalizedRateMap: Record<string, number>, code: string): number | undefined => {
  if (code.includes('+')) {
    const parts = code
      .split('+')
      .map((part) => normalizedRateMap[part.trim().toUpperCase()])
      .filter((rate): rate is number => rate !== undefined);
    return parts.length > 0 ? parts.reduce((sum, rate) => sum + rate, 0) : undefined;
  }
  return normalizedRateMap[code.trim().toUpperCase()];
};

const normalizedRateFor = (
  normalizedRateMap: Record<string, number>,
  markupFactor: number,
  code: string | undefined,
): number | undefined => {
  if (!code) return undefined;
  const base = normalizedBaseRateFor(normalizedRateMap, code);
  if (base == null) return undefined;
  return markedUpRate(base, markupFactor);
};

// ── Roads ────────────────────────────────────────────────────────────────────

/**
 * Carriageway Summary section key → matrix column. The Bill Summary already
 * groups every roads item under these keys, so the matrix is a transpose of it
 * (roads down the side, its sections across the top) rather than a
 * re-classification. An unrecognised key falls into `roads-other`, so a new
 * section added to the Bill Summary shows up rather than silently vanishing.
 */
const ROADS_SECTION_TO_COLUMN: Record<string, string> = {
  'sec-site-clearance': 'roads-clearance',
  'sec-excavating-filling': 'roads-earthworks',
  'sec-carriageways': 'roads-carriageways',
  'sec-footpaths': 'roads-footpaths',
  'sec-kerbs': 'roads-kerbs',
  'sec-ironwork': 'roads-ironwork',
  'sec-white-lining': 'roads-lining',
  'sec-street-nameplates': 'roads-furniture',
  'sec-tree-bollards': 'roads-furniture',
  'sec-post-rail': 'roads-furniture',
  'sec-armco': 'roads-furniture',
  'sec-signage': 'roads-furniture',
  'sec-remedials': 'roads-remedials',
};

export const ROADS_COLUMNS: readonly SectionMatrixColumn[] = [
  {
    key: 'roads-clearance',
    group: 'Earthworks',
    label: 'Site Clearance',
    description: 'Site strip to remove vegetation and topsoil excavation (Site Clearance section).',
  },
  {
    key: 'roads-earthworks',
    group: 'Earthworks',
    label: 'Excavate & Fill',
    description:
      'Excavate to reduce levels, filling with site-won subsoil or recycled 6F2, level & compact formation (Excavating & Filling section).',
  },
  {
    key: 'roads-carriageways',
    group: 'Construction',
    label: 'Carriageways',
    description:
      'Every carriageway build-up layer — membrane, capping, sub-base, base / binder / surface course and their extra-overs (Carriageways section).',
  },
  {
    key: 'roads-footpaths',
    group: 'Construction',
    label: 'Footpaths',
    description:
      'Footway, crossover, cycleway and verge build-ups, including tactile and corduroy paving (Footpaths section).',
  },
  {
    key: 'roads-kerbs',
    group: 'Construction',
    label: 'Kerbs & Edgings',
    description: 'Kerbs, races, channels, edgings and their extra-overs (Kerbs & Edgings section).',
  },
  {
    key: 'roads-ironwork',
    group: 'Finishings',
    label: 'Ironwork',
    description: 'Raising, lowering and adjusting ironwork to final levels (Ironwork section).',
  },
  {
    key: 'roads-lining',
    group: 'Finishings',
    label: 'White Lining',
    description: 'Road markings and white lining (White Lining section).',
  },
  {
    key: 'roads-furniture',
    group: 'Finishings',
    label: 'Furniture & Signage',
    description:
      'Street nameplates, tree bollards, post & rail, Armco barriers and signage (four Bill Summary sections combined).',
  },
  {
    key: 'roads-remedials',
    group: 'Finishings',
    label: 'Remedials',
    description: 'Remedial works allowances (Remedials section).',
  },
  {
    key: 'roads-other',
    group: 'Finishings',
    label: 'Other',
    description: 'Any Bill Summary section not mapped to a column above.',
  },
] as const;

export const buildRoadsMatrix = (
  roadsData: RoadsData | undefined,
  pricing: MatrixPricing,
): SectionMatrix => {
  const columns = [...ROADS_COLUMNS];
  const state: MatrixBuilderState = { columns, rows: [], measuredItems: 0, pricedItems: 0 };
  const roads = roadsData?.carriagewayFootways.table ?? [];
  const sections = buildRoadsBillSummarySections(roadsData);
  // RoadsBillSummary.tsx's own `resourceCodeMap` normalizes (trim + uppercase) both sides
  // of its rate lookup — see the comment on `baseRateFor` above — so this matrix must too,
  // or evaluate codes that only match case-insensitively (e.g. mapping constants like
  // "ALP017c" against an upper-cased item-library resourceCode) price to £0 here while the
  // real bill prices them fine, understating this matrix's (and Sum Analysis's) total
  // relative to Roads Bill Summary's TOTAL CARRIAGEWAY row.
  const normalizedRateMap = buildNormalizedRateMap(pricing.rateMap);

  const rowByRoadId = new Map<string, SectionMatrixRow>();
  roads.forEach((road, i) => {
    const row = makeRow(`road-${road.id}`, road.roadName || `Road ${i + 1}`, columns, {
      sublabel: road.totalRoadArea
        ? `${road.totalRoadArea.toLocaleString('en-GB')} m² carriageway`
        : undefined,
    });
    rowByRoadId.set(road.id, row);
    state.rows.push(row);
  });

  sections.forEach((section) => {
    const columnKey = ROADS_SECTION_TO_COLUMN[section.key] ?? 'roads-other';
    section.children.forEach((item) => {
      if (item.rowType !== 'item') return;
      const rate = normalizedRateFor(normalizedRateMap, pricing.markupFactor, item.evaluateCode);
      Object.entries(item.values).forEach(([roadId, value]) => {
        const qty = value?.qty ?? 0;
        if (!qty) return;
        state.measuredItems += 1;
        if (rate != null) state.pricedItems += 1;
        const row = rowByRoadId.get(roadId);
        if (!row) return;
        pushLine(row, columnKey, {
          description: item.description,
          qty,
          unit: item.unit,
          rate,
          amount: rate == null ? 0 : qty * rate,
          source: section.description,
          evaluateCode: item.evaluateCode,
        });
      });
    });
  });

  return finalise(
    'roads',
    'Roads',
    'Road',
    state,
    sumRoadsBillSummaryAmount(sections, (code) => normalizedRateFor(normalizedRateMap, pricing.markupFactor, code)),
    {
      note:
        'Columns are the Carriageway Summary\'s own sections, so this matrix is a transpose of the Roads Bill Summary — roads down the side instead of across the top. Rows priced at £0 are measured items with no item-library rate yet.',
      emptyHint:
        'No roads entered yet. Add roads on the Roads take-off page and this matrix will build itself.',
    },
  );
};

// ── Main Services ────────────────────────────────────────────────────────────

const MAIN_SERVICE_GROUP_TO_GROUP_HEADER: Record<string, string> = {
  water: 'Utilities',
  electric: 'Utilities',
  gas: 'Utilities',
  'bt-ducting': 'Comms',
  'bt-ducting-stakkabox': 'Comms',
  'virgin-media': 'Comms',
  streetlighting: 'Lighting',
  'combined-service': 'Ducts & Crossings',
  'road-crossings': 'Ducts & Crossings',
  'red-electric': 'Ducts & Crossings',
  'eo-live-footway': 'Extra Over',
};

// Exported (rather than kept inline in `buildMainServicesMatrix` below) so
// `dynamicBillSummaryRunMatrix.ts` can offer the same "Main Services" column
// list in the Add/Edit Item modal's Run Matrix Mapping section without
// rebuilding the whole live matrix.
export const MAIN_SERVICES_COLUMNS: readonly SectionMatrixColumn[] = MAIN_SERVICE_TRENCHING_MAPPING.map(
  (group) => ({
    key: group.id,
    group: MAIN_SERVICE_GROUP_TO_GROUP_HEADER[group.id] ?? 'Other',
    label: group.label,
    description: `${group.label} — ${group.items.map((i) => i.description).join('; ')}.`,
  }),
);

export const buildMainServicesMatrix = (
  mainServicesTable: Record<string, Record<string, number>> | undefined,
  roadsData: RoadsData | undefined,
  pricing: MatrixPricing,
): SectionMatrix => {
  const columns: SectionMatrixColumn[] = [...MAIN_SERVICES_COLUMNS];
  const state: MatrixBuilderState = { columns, rows: [], measuredItems: 0, pricedItems: 0 };
  const quantities = mainServicesTable ?? {};
  const roads = roadsData?.carriagewayFootways.table ?? [];
  // MainServicesBillSummary.tsx's own `rateMap` normalizes (trim + uppercase) both sides of
  // its lookup, same as Roads — see the comment on `baseRateFor` above — so this matrix must
  // too, or it undercounts relative to that bill's TOTAL MAIN SERVICES row.
  const normalizedRateMap = buildNormalizedRateMap(pricing.rateMap);

  const rowByRoadId = new Map<string, SectionMatrixRow>();
  roads.forEach((road, i) => {
    const row = makeRow(`ms-${road.id}`, road.roadName || `Road ${i + 1}`, columns);
    rowByRoadId.set(road.id, row);
    state.rows.push(row);
  });

  // Quantities can carry road ids that no longer exist in the take-off (a road
  // deleted after its services were measured). The Bill Summary still sums them,
  // so they get their own row here rather than quietly dropping out of the total.
  const orphanRow = makeRow('ms-orphan', 'Unassigned road', columns, {
    sublabel: 'Quantities whose road is no longer in the take-off',
  });

  MAIN_SERVICE_TRENCHING_MAPPING.forEach((group) => {
    group.items.forEach((item) => {
      const rate = normalizedRateFor(
        normalizedRateMap,
        pricing.markupFactor,
        findMainServiceTrenchingEvaluateCode(item.description),
      );
      Object.entries(quantities[item.id] ?? {}).forEach(([roadId, qty]) => {
        if (!qty) return;
        state.measuredItems += 1;
        if (rate != null) state.pricedItems += 1;
        pushLine(rowByRoadId.get(roadId) ?? orphanRow, group.id, {
          description: item.description,
          qty,
          unit: item.unit,
          rate,
          amount: rate == null ? 0 : qty * rate,
          source: group.label,
          evaluateCode: findMainServiceTrenchingEvaluateCode(item.description),
        });
      });
    });
  });

  if (orphanRow.measuredCount > 0) state.rows.push(orphanRow);

  return finalise(
    'main-services',
    'Main Services',
    'Road',
    state,
    sumMainServicesAmount(quantities, (code) => normalizedRateFor(normalizedRateMap, pricing.markupFactor, code)),
    {
      note:
        'Main service trenching is measured per road on the take-off page, so the matrix shows utility cost per road. Substation base and access have their own matrix.',
      emptyHint:
        'No main service trenching measured yet. Enter quantities on the Main Services & Substation page.',
    },
  );
};

// ── Substation ───────────────────────────────────────────────────────────────

export const buildSubstationMatrix = (
  substationTable: Record<string, FoulBuildUpRowData> | undefined,
  pricing: MatrixPricing,
): SectionMatrix => {
  const columns = [...WORK_CATEGORY_COLUMNS];
  const state: MatrixBuilderState = { columns, rows: [], measuredItems: 0, pricedItems: 0 };
  const saved = substationTable ?? {};

  let currentRow: SectionMatrixRow | null = null;
  SUBSTATION_BASE_ROW_DEFS.forEach((def) => {
    if (def.rowType === 'sectionHeader') {
      currentRow = makeRow(def.id, def.description, columns);
      state.rows.push(currentRow);
      return;
    }
    const qty = saved[def.id]?.quant ?? 0;
    if (!qty || !currentRow) return;
    state.measuredItems += 1;
    // Same static-description lookup `SubstationBillSummary.tsx` uses — the
    // configured mm/metre values change the row's *displayed* wording only.
    const code = findSubstationBaseEvaluateCode(def.description);
    const rate = rateFor(pricing, code);
    if (rate != null) state.pricedItems += 1;
    pushLine(currentRow, classifyWorkCategory(def.description), {
      description: def.description,
      qty,
      unit: def.unit,
      rate,
      amount: rate == null ? 0 : qty * rate,
      source: 'Substation',
      evaluateCode: code,
    });
  });

  // Drop element rows with nothing measured against them.
  state.rows = state.rows.filter((row) => row.measuredCount > 0);

  return finalise(
    'substation',
    'Substation',
    'Element',
    state,
    sumSubstationAmount(saved, (code) => rateFor(pricing, code)),
    {
      note:
        'The substation build-up has no second axis of its own, so its items are grouped into work categories by description — open a cell to see exactly which items it holds.',
      emptyHint:
        'No substation quantities entered yet. Enter them on the Main Services & Substation page.',
    },
  );
};

// ── POS ──────────────────────────────────────────────────────────────────────

export const POS_COLUMNS: readonly SectionMatrixColumn[] = [
  {
    key: 'pos-ground',
    group: 'Ground & Soils',
    label: 'Ground Prep & Soils',
    description:
      'Geotextile membrane, "No Dig" layer, imported / site-won capping subsoil and topsoil spreading — every row above the path sub-headings.',
  },
  {
    key: 'pos-gravel',
    group: 'Paths',
    label: 'Gravel Path',
    description: 'POS GRAVEL PATH — formation, MOT Type 1, bound gravel surfacing and timber edgings.',
  },
  {
    key: 'pos-macadam',
    group: 'Paths',
    label: 'Macadam Path',
    description: 'POS MACADAM PATH — formation, MOT Type 1, binder and surface course, tack coat and edgings.',
  },
  {
    key: 'pos-leap',
    group: 'Paths',
    label: 'LEAP',
    description:
      'LEAP (Local Equipped Area for Play). The template row here is the standard exclusion note, so this column is normally £0.',
  },
] as const;

const posColumnForHeader = (description: string): string => {
  const d = description.toUpperCase();
  if (d.includes('GRAVEL')) return 'pos-gravel';
  if (d.includes('MACADAM')) return 'pos-macadam';
  if (d.includes('LEAP')) return 'pos-leap';
  return 'pos-ground';
};

export const buildPosMatrix = (
  pos:
    | {
        table?: Record<string, FoulBuildUpRowData>;
        config?: { sectionCount?: number; sectionTitles?: Record<string, string>; rowThicknessMm?: Record<string, number>; rowText?: Record<string, string>; geotextileIncluded?: Record<string, boolean> };
      }
    | undefined,
  pricing: MatrixPricing,
): SectionMatrix => {
  const columns = [...POS_COLUMNS];
  const state: MatrixBuilderState = { columns, rows: [], measuredItems: 0, pricedItems: 0 };
  const saved = pos?.table ?? {};
  const sectionCount = pos?.config?.sectionCount ?? DEFAULT_POS_SECTIONS;
  const rowThicknessMm = pos?.config?.rowThicknessMm ?? {};
  const rowText = pos?.config?.rowText ?? {};
  const geotextileIncluded = pos?.config?.geotextileIncluded ?? {};
  const sectionTitles = pos?.config?.sectionTitles ?? {};

  let currentRow: SectionMatrixRow | null = null;
  let currentColumn = 'pos-ground';

  getPOSRowDefs(sectionCount).forEach((def) => {
    if (def.rowType === 'empty') return;
    if (def.rowType === 'posHeader') {
      currentRow = makeRow(def.id, sectionTitles[def.id] ? `${def.description} - ${sectionTitles[def.id]}` : def.description, columns);
      currentColumn = 'pos-ground';
      state.rows.push(currentRow);
      return;
    }
    if (def.rowType === 'subHeader' || def.rowType === 'leapHeader') {
      currentColumn = posColumnForHeader(def.description);
      return;
    }
    const qty = saved[def.id]?.quant ?? 0;
    if (!qty || !currentRow) return;
    if (isPosGeotextileExcluded(def.id, geotextileIncluded)) return;
    state.measuredItems += 1;
    // Same configured-description resolution `POSBillSummary.tsx` uses — POS
    // evaluate codes are looked up by the *configured* wording, not the template.
    const field = getPOSConfigurableField(def.id);
    const mm = field ? rowThicknessMm[def.id] : undefined;
    const text = field ? rowText[def.id] : undefined;
    const description =
      field && (mm || text)
        ? field.describe(mm ?? field.placeholderMm ?? 'xx', text)
        : def.description;
    const code = findPosEvaluateCode(description);
    const rate = rateFor(pricing, code);
    if (rate != null) state.pricedItems += 1;
    pushLine(currentRow, currentColumn, {
      description,
      qty,
      unit: def.unit,
      rate,
      amount: rate == null ? 0 : qty * rate,
      source: 'POS',
      evaluateCode: code,
    });
  });

  state.rows = state.rows.filter((row) => row.measuredCount > 0);

  return finalise(
    'pos',
    'Public Open Space',
    'Area',
    state,
    sumPosAmount(saved, sectionCount, rowThicknessMm, rowText, (code) => rateFor(pricing, code), geotextileIncluded),
    {
      note:
        'One row per Public Open Space, with the take-off\'s own path sub-headings as columns. Rates are resolved from each row\'s configured description, exactly as the POS Bill Summary does.',
      emptyHint:
        'No POS quantities entered yet. Enter them on the POS page.',
    },
  );
};

// ── Compound ─────────────────────────────────────────────────────────────────

export const buildCompoundMatrix = (
  compound:
    | { table?: Record<string, FoulBuildUpRowData>; config?: CompoundConfig | Record<string, never> }
    | undefined,
  pricing: MatrixPricing,
): SectionMatrix => {
  const columns = [...WORK_CATEGORY_COLUMNS];
  const state: MatrixBuilderState = { columns, rows: [], measuredItems: 0, pricedItems: 0 };
  const saved = compound?.table ?? {};
  const config = (compound?.config ?? {}) as CompoundConfig;
  const { sectionLabels, itemLabels } = getCompoundLabels(config);

  let currentGroup = '';
  let currentRow: SectionMatrixRow | null = null;

  COMPOUND_ROW_DEFS.forEach((def) => {
    if (def.rowType === 'compoundHeader') {
      currentGroup = def.description;
      currentRow = null;
      return;
    }
    if (def.rowType === 'sectionHeader') {
      currentRow = makeRow(def.id, sectionLabels[def.id] ?? def.description, columns, {
        sublabel: currentGroup,
      });
      state.rows.push(currentRow);
      return;
    }
    if (def.rowType !== 'data') return;
    const qty = saved[def.id]?.quant ?? 0;
    if (!qty || !currentRow) return;
    state.measuredItems += 1;
    const description = itemLabels[def.id] ?? def.description;
    const code = findCompoundEvaluateCode(description);
    const rate = rateFor(pricing, code);
    if (rate != null) state.pricedItems += 1;
    pushLine(currentRow, classifyWorkCategory(description), {
      description,
      qty,
      unit: def.unit,
      rate,
      amount: rate == null ? 0 : qty * rate,
      source: currentGroup,
      evaluateCode: code,
    });
  });

  state.rows = state.rows.filter((row) => row.measuredCount > 0);

  return finalise(
    'compound',
    'Compound',
    'Element',
    state,
    sumCompoundAmount(saved, config, (code) => rateFor(pricing, code)),
    {
      note:
        'One row per compound element, with items grouped into work categories by description. The source take-off spec has no evaluate codes assigned to any Compound row yet, so amounts read £0 until rates are added — the measured / priced counts above make that explicit.',
      emptyHint:
        'No compound quantities entered yet. Enter them on the Compound page.',
    },
  );
};

// ── Spoil Reconciliation ─────────────────────────────────────────────────────

const SPOIL_COLUMNS: readonly SectionMatrixColumn[] = [
  {
    key: 'topsoilStrip',
    group: 'Topsoil (m³)',
    label: 'Strip',
    description: 'Topsoil stripped from this area.',
  },
  {
    key: 'topsoilReuse',
    group: 'Topsoil (m³)',
    label: 'Re-use',
    description: 'Topsoil spread back within this area.',
  },
  {
    key: 'topsoilSurplus',
    group: 'Topsoil (m³)',
    label: 'Surplus',
    description: 'Strip − Re-use. Positive = surplus to dispose of; negative = import needed.',
  },
  {
    key: 'subsoilCut',
    group: 'Subsoil (m³)',
    label: 'Cut / Arisings',
    description: 'Subsoil dug out of this area.',
  },
  {
    key: 'subsoilFill',
    group: 'Subsoil (m³)',
    label: 'Fill',
    description: 'Subsoil placed back within this area.',
  },
  {
    key: 'subsoilSurplus',
    group: 'Subsoil (m³)',
    label: 'Surplus',
    description: 'Cut − Fill. Positive = surplus to dispose of; negative = import needed.',
  },
] as const;

export const buildSpoilMatrix = (source: SpoilReconciliationSource): SectionMatrix => {
  const columns = [...SPOIL_COLUMNS];
  const state: MatrixBuilderState = { columns, rows: [], measuredItems: 0, pricedItems: 0 };
  const summary = buildSpoilReconciliationSummary(source);

  summary.rows.forEach((area) => {
    const row = makeRow(`spoil-${area.key}`, area.label, columns, { sublabel: area.origin });
    const setCell = (key: string, value: number | null, unitLabel: string): void => {
      const cell = row.cells[key];
      if (!cell) return;
      if (value == null) {
        cell.notMeasured = true;
        return;
      }
      cell.total = value;
      cell.lines = [
        { description: `${area.label} — ${unitLabel}`, qty: value, unit: 'm³', amount: value, source: area.origin },
      ];
    };

    const topsoilSurplus =
      area.topsoilStrip == null && area.topsoilReuse == null
        ? null
        : (area.topsoilStrip ?? 0) - (area.topsoilReuse ?? 0);
    const subsoilSurplus =
      area.subsoilCut == null && area.subsoilFill == null
        ? null
        : (area.subsoilCut ?? 0) - (area.subsoilFill ?? 0);

    setCell('topsoilStrip', area.topsoilStrip, 'topsoil strip');
    setCell('topsoilReuse', area.topsoilReuse, 'topsoil re-use');
    setCell('topsoilSurplus', topsoilSurplus, 'topsoil surplus');
    setCell('subsoilCut', area.subsoilCut, 'subsoil cut');
    setCell('subsoilFill', area.subsoilFill, 'subsoil fill');
    setCell('subsoilSurplus', subsoilSurplus, 'subsoil surplus');

    // The row total is what actually leaves (or has to come to) site.
    row.total = (topsoilSurplus ?? 0) + (subsoilSurplus ?? 0);
    if (
      area.topsoilStrip != null || area.topsoilReuse != null ||
      area.subsoilCut != null || area.subsoilFill != null
    ) {
      state.measuredItems += 1;
    }
    state.rows.push(row);
  });

  const matrix = finalise('spoil', 'Spoil Reconciliation', 'Area', state, 0, {
    valueKind: 'volume',
    totalHeader: 'Net surplus (m³)',
    note:
      'Volumes, not money — this section has no rates. Topsoil for roads and footpaths is measured together on the Spoil Reconciliation page, so that combined figure sits on the Roads row. Net surplus is topsoil surplus + subsoil surplus: positive means muck away, negative means import.',
    emptyHint: 'No spoil volumes yet — they come from the Roads, Foul, Storm and Ponds pages plus the Spoil Reconciliation page\'s own inputs.',
  });

  // Column totals must respect the surplus columns being derived, and there is
  // no bill to reconcile against, so both sides of the identity are the matrix.
  return { ...matrix, billTotal: matrix.grandTotal, variance: 0 };
};


// ── Preliminaries ────────────────────────────────────────────────────────────

/**
 * Prelims have no physical unit — what an estimator wants to see is **how** each
 * part of the preliminaries is charged, because time-related costs move with the
 * programme while fixed costs do not. So the columns are charge types, taken
 * straight from each row's own unit.
 */
export const PRELIMS_COLUMNS: readonly SectionMatrixColumn[] = [
  {
    key: 'prelims-fixed',
    group: 'Charge type',
    label: 'Fixed (Item)',
    description: 'One-off lump-sum items — unit "Item". These do not move with the programme.',
  },
  {
    key: 'prelims-time',
    group: 'Charge type',
    label: 'Time-related (Wks)',
    description:
      'Weekly / time-related costs — unit "Wks". These are the rows that move when the programme changes.',
  },
  {
    key: 'prelims-enumerated',
    group: 'Charge type',
    label: 'Enumerated (Nr)',
    description: 'Counted items — unit "Nr" (applications, tests, visits, posts…).',
  },
  {
    key: 'prelims-linear',
    group: 'Charge type',
    label: 'Linear (m)',
    description: 'Measured by length — fencing and similar.',
  },
  {
    key: 'prelims-area',
    group: 'Charge type',
    label: 'Area (m²)',
    description: 'Measured by area.',
  },
  {
    key: 'prelims-other',
    group: 'Charge type',
    label: 'Other',
    description: 'Any unit not covered above.',
  },
] as const;

const prelimsColumnForUnit = (unit: string): string => {
  const u = (unit || '').trim().toLowerCase();
  if (u === 'item' || u === 'sum') return 'prelims-fixed';
  if (u.startsWith('wk') || u.startsWith('week')) return 'prelims-time';
  if (u === 'nr' || u === 'no' || u === 'each') return 'prelims-enumerated';
  if (u === 'm' || u === 'lm') return 'prelims-linear';
  if (u === 'm2' || u === 'm²') return 'prelims-area';
  return 'prelims-other';
};

export const buildPrelimsMatrix = (
  savedItems: PrelimsItemRecord[] | undefined,
  pricing: MatrixPricing,
): SectionMatrix => {
  const columns = [...PRELIMS_COLUMNS];
  const state: MatrixBuilderState = { columns, rows: [], measuredItems: 0, pricedItems: 0 };

  // Preliminaries' own bill (`PrelimsBillSummary.tsx`'s `resourceCodeMap`) is the
  // one Bill Summary that normalizes both sides of its item-library lookup
  // (trim + uppercase) — every other section's rate lookup is exact-only (see
  // `baseRateFor`), so that normalization is built just for this domain here,
  // from the shared raw `pricing.rateMap`, rather than applied globally.
  // Last-wins on a collision, same as `resourceCodeMap`'s `Map.set` — a later
  // duplicate resourceCode differing only by case/whitespace overwrites rather
  // than being ignored.
  const normalizedRateMap: Record<string, number> = {};
  Object.entries(pricing.rateMap).forEach(([code, rate]) => {
    normalizedRateMap[code.trim().toUpperCase()] = rate;
  });

  const savedById = new Map<string, PrelimsItemRecord>();
  (savedItems ?? []).forEach((item) => savedById.set(item.id, item));

  // One row per bill group (or per category for items that hang directly off
  // one), created on first use so only measured groups appear, with a band row
  // per category — the same order the Preliminary Items bill renders in.
  const rowByKey = new Map<string, SectionMatrixRow>();
  const bandRowByCategory = new Map<string, SectionMatrixRow>();

  forEachPrelimsItem(savedById, (ref, saved, item) => {
    state.measuredItems += 1;

    let bandRow = bandRowByCategory.get(ref.categoryId);
    if (!bandRow) {
      bandRow = makeRow(`prelims-band-${ref.categoryId}`, ref.categoryLabel, columns, { rowKind: 'band' });
      bandRowByCategory.set(ref.categoryId, bandRow);
      state.rows.push(bandRow);
    }

    const rowKey = ref.groupId
      ? `prelims-${ref.categoryId}-${ref.groupId}`
      : `prelims-${ref.categoryId}-items`;
    let row = rowByKey.get(rowKey);
    if (!row) {
      row = makeRow(rowKey, ref.groupLabel ?? ref.categoryLabel, columns, {
        sublabel: ref.groupLabel ? ref.categoryLabel : undefined,
      });
      rowByKey.set(rowKey, row);
      state.rows.push(row);
    }

    // Variant rows (e.g. "CBR testing option(s)") resolve their code and label
    // from the saved selection, same as PrelimsBillSummary.tsx.
    const selectedVariant = item.variants?.find((v) => v.code === saved.code);
    const code = item.variants ? selectedVariant?.code ?? item.code : item.code;
    const description = selectedVariant?.label ?? item.description;
    const libRate = normalizedRateMap[(code || '').trim().toUpperCase()] ?? 0;
    const baseRate = saved.rate > 0 ? saved.rate : libRate;
    if (baseRate > 0) state.pricedItems += 1;
    // Rate is rounded to 2dp before extension, matching `PrelimsBillSummary.tsx`'s
    // own per-row rate — so this matrix's Amount reconciles against the bill
    // exactly. Deliberately NOT the shared `markedUpRate()` other sections use:
    // that helper skips rounding entirely when markupFactor === 1 (fine for a
    // nett section, which never rounds its rate either), but Preliminaries'
    // own bill (`PrelimsBillSummary.tsx`, `sumPrelimsAmount`) always rounds its
    // rate to 2dp, even with OH&P/Preliminaries both off — using the shared
    // helper here left a residual penny-level variance whenever markupFactor
    // was exactly 1.
    const rate = parseFloat((baseRate * pricing.markupFactor).toFixed(2));
    const amount = saved.quantity * rate;

    pushLine(row, prelimsColumnForUnit(saved.unit || item.unit), {
      description,
      qty: saved.quantity,
      unit: saved.unit || item.unit,
      rate,
      amount,
      source: ref.groupLabel ? `${ref.categoryLabel} › ${ref.groupLabel}` : ref.categoryLabel,
      evaluateCode: code || undefined,
    });
    // The band row's own Total — each category's section total, so it can be
    // read directly rather than only inferred from the combined grand total.
    bandRow.total += amount;
  });

  return finalise(
    'prelims',
    'Preliminaries',
    'Item group',
    state,
    sumPrelimsAmount(
      savedItems,
      // `sumPrelimsAmount` already normalizes the code (`.trim().toUpperCase()`)
      // before calling this — `normalizedRateMap`'s keys are normalized the same
      // way, so this is a direct lookup, not another `baseRateFor` call.
      (code) => normalizedRateMap[code],
      pricing.markupFactor,
    ),
    {
      note:
        'Columns are charge types read off each row\'s own unit, so the split between fixed and time-related preliminaries is visible at a glance. Rates are the estimator\'s own entered rate where there is one, otherwise the item-library rate — same rule the Preliminary Items bill uses. That bill always shows rates with OH&P and Preliminaries markup applied, so it matches this matrix when both markup toggles are on.',
      emptyHint:
        'No preliminaries measured yet. Enter quantities on the Preliminary Items page.',
    },
  );
};

// ── Build-up domains (Foul: Pump Station / Rising Main; Storm: attenuation) ───

/** The row-definition fields both `BuildUpRowDef` and `StormBuildUpRowDef` share. */
interface BuildUpRowLike {
  id: string;
  description: string;
  unit: string;
  isHeader?: boolean;
  isSubHeader?: boolean;
  isSeparator?: boolean;
  noQuant?: boolean;
}

interface BuildUpDomainSpec {
  /** Band heading, e.g. "PUMP STATION". */
  band: string;
  /** The exact tree `FoulBuildUpBillSummary.tsx`/`StormBuildUpBillSummary.tsx`
   * render — built once (via `buildBuildUpSummaryData`/`buildStormBuildUpSummaryData`)
   * and walked directly for both this matrix's rows/cells and `billTotal` below,
   * so the two can no longer independently derive different quantities/codes
   * for the same row and drift apart. */
  tree: BuildUpSummaryTreeRow[];
  /** Row label for items that sit above the domain's first heading. */
  generalLabel: string;
  /** Σ qty × rate over `tree` — the same figure the Bill Summary's own TOTAL
   * row shows, since it's the same tree. */
  billTotal: number;
}

/** Shared shape of the Foul / Storm build-up Bill Summary trees. */
interface BuildUpSummaryTreeRow {
  /** Group (header) rows only — Foul uses `reference`, Storm uses `description`. */
  reference?: string;
  description?: string;
  quantity?: string;
  unit?: string;
  evaluateCode?: string;
  /** Bold label row with no qty/rate (Storm only) — see `StormSummaryRow`. */
  isSubHeader?: boolean;
  children?: BuildUpSummaryTreeRow[];
}

/**
 * Σ qty × rate over a build-up Bill Summary tree — the same traversal
 * `FoulBuildUpBillSummary.tsx` / `StormBuildUpBillSummary.tsx` use for their own
 * TOTAL row, so it is an independent check on the row-by-row allocation below.
 */
const sumBuildUpTree = (
  rows: BuildUpSummaryTreeRow[],
  pricing: MatrixPricing,
): number =>
  rows.reduce((acc, row) => {
    if (row.children) return acc + sumBuildUpTree(row.children, pricing);
    const qty = parseFloat(row.quantity ?? '');
    if (!Number.isFinite(qty)) return acc;
    const rate = rateFor(pricing, row.evaluateCode);
    return rate == null ? acc : acc + qty * rate;
  }, 0);

/**
 * Lays a set of build-up domains out as one matrix: a band per domain, a row per
 * heading inside it (chamber, tank, pond…), work categories across the top.
 */
const buildBuildUpMatrix = (
  key: SiteWorksSectionKey,
  title: string,
  unitHeader: string,
  domains: BuildUpDomainSpec[],
  pricing: MatrixPricing,
  meta: { note: string; emptyHint: string },
): SectionMatrix => {
  const columns = [...WORK_CATEGORY_COLUMNS];
  const state: MatrixBuilderState = { columns, rows: [], measuredItems: 0, pricedItems: 0 };
  let billTotal = 0;

  domains.forEach((domain) => {
    billTotal += domain.billTotal;
    const bandRow = makeRow(`${domain.band}-band`, domain.band, columns, { rowKind: 'band' });
    const domainRows: SectionMatrixRow[] = [];
    let currentRow: SectionMatrixRow | null = null;

    const ensureGeneralRow = (): SectionMatrixRow => {
      if (!currentRow) {
        currentRow = makeRow(`${domain.band}-general`, domain.generalLabel, columns, {
          sublabel: domain.band,
        });
        domainRows.push(currentRow);
      }
      return currentRow;
    };

    // Walks the same tree `FoulBuildUpBillSummary.tsx`/`StormBuildUpBillSummary.tsx`
    // render (already pruned of zero-quantity rows) — a group node (header) starts
    // a new matrix row, a leaf item is priced and pushed into whichever row is
    // currently open, exactly mirroring `sumBuildUpTree`'s own traversal below.
    let groupIndex = 0;
    const walk = (nodes: BuildUpSummaryTreeRow[]): void => {
      nodes.forEach((node) => {
        if (node.children) {
          groupIndex += 1;
          currentRow = makeRow(`${domain.band}-group-${groupIndex}`, node.reference ?? node.description ?? '', columns, {
            sublabel: domain.band,
          });
          domainRows.push(currentRow);
          walk(node.children);
          return;
        }
        if (node.isSubHeader) return;
        const qty = parseFloat(node.quantity ?? '');
        if (!Number.isFinite(qty) || qty === 0) return;
        state.measuredItems += 1;
        const code = node.evaluateCode;
        const rate = rateFor(pricing, code);
        if (rate != null) state.pricedItems += 1;
        const description = node.description ?? '';
        pushLine(ensureGeneralRow(), classifyWorkCategory(description), {
          description,
          qty,
          unit: node.unit ?? '',
          rate,
          amount: rate == null ? 0 : qty * rate,
          source: domain.band,
          evaluateCode: code,
        });
      });
    };
    walk(domain.tree);

    const measured = domainRows.filter((row) => row.measuredCount > 0);
    if (measured.length === 0) return;
    // The band row's own Total — e.g. Pump Station's vs Rising Main's own
    // section total — so each reconciles against its own Bill Summary tab
    // rather than only the combined "Pump Station & Rising Main" grand total.
    bandRow.total = measured.reduce((sum, row) => sum + row.total, 0);
    state.rows.push(bandRow, ...measured);
  });

  const matrix = finalise(key, title, unitHeader, state, billTotal, meta);
  return matrix;
};

export const buildFoulBuildUpsMatrix = (
  pumpStation: { table?: Record<string, FoulBuildUpRowData>; config?: PumpChamberConfig | Record<string, never> } | undefined,
  risingMain: { table?: Record<string, FoulBuildUpRowData> } | undefined,
  pricing: MatrixPricing,
  /** Foul Manhole Schedule's Ladders setting — switches the Pump Station ladder rows' material wording. */
  ladders?: string,
): SectionMatrix => {
  // Same guard FoulSewersTakeoff.tsx / FoulSewersBillSummary.tsx use: an empty
  // pump-chamber config has no coverLevel, and the Wet Well / mesh rows' codes
  // need a real one to resolve.
  const pumpChamberConfig: PumpChamberConfigForEvaluateCode | undefined =
    (pumpStation?.config as PumpChamberConfig)?.coverLevel !== undefined
      ? (pumpStation?.config as PumpChamberConfig)
      : undefined;

  // Built once each — the exact tree FoulBuildUpBillSummary.tsx renders for the
  // Pump Station / Rising Main tabs — then reused for both this matrix's own
  // rows/cells and its billTotal, so the two can never independently diverge.
  const pumpStationTree = buildBuildUpSummaryData(
    applyPumpStationSectionTitles(
      applyPumpStationLadderType(PUMP_STATION_ROWS, ladders, pumpStation?.config as PumpChamberConfig | undefined),
      (pumpStation?.config as PumpChamberConfig | undefined)?.sectionTitles,
    ),
    pumpStation?.table,
    pumpChamberConfig,
  );
  const risingMainTree = buildBuildUpSummaryData(RISING_MAIN_ROWS, risingMain?.table);

  return buildBuildUpMatrix(
    'foul-build-ups',
    'Pump Station & Rising Main',
    'Element',
    [
      {
        band: 'PUMP STATION',
        tree: pumpStationTree,
        generalLabel: 'Site works & slab',
        billTotal: sumBuildUpTree(pumpStationTree, pricing),
      },
      {
        band: 'RISING MAIN',
        tree: risingMainTree,
        generalLabel: 'Rising main',
        billTotal: sumBuildUpTree(risingMainTree, pricing),
      },
    ],
    pricing,
    {
      note:
        'The Foul Sewers build-ups: one row per pump-station element (pump chamber, valve chamber, flow meter chamber, kiosk base, storage tank) and per rising-main heading, with items grouped into work categories by description. Reconciles against the Pump Station and Rising Main tabs of the Foul Sewers Bill Summary.',
      emptyHint:
        'No pump station or rising main quantities entered yet. Enter them on the Foul Sewers page.',
    },
  );
};

export interface StormBuildUpsInput {
  pondsSwales?: {
    table?: Record<string, FoulBuildUpRowData>;
    config?: {
      pondSectionCount?: number;
      pondTopsoilThicknessMm?: Record<string, number>;
      swaleSectionCount?: number;
      swaleTopsoilThicknessMm?: Record<string, number>;
      sectionTitles?: Record<string, string>;
    };
  };
  cellular?: { table?: Record<string, FoulBuildUpRowData>; config?: { sectionCount?: number; supplierName?: string; crateDims?: Record<string, number>; sectionTitles?: Record<string, string> } };
  weholite?: {
    table?: Record<string, FoulBuildUpRowData>;
    config?: { sectionCount?: number; supplierName?: string; weholiteConfigPct?: Record<string, number>; sectionTitles?: Record<string, string> };
  };
  conc?: {
    table?: Record<string, FoulBuildUpRowData>;
    config?: { sectionCount?: number; supplierName?: string; concConfigPct?: Record<string, number>; sectionTitles?: Record<string, string>; drawingRefs?: Record<string, string> };
  };
  boxCulverts?: {
    table?: Record<string, FoulBuildUpRowData>;
    config?: {
      sectionCount?: number;
      culvertConfigMm?: Record<string, number>;
      culvertBackfillType?: Record<string, string>;
    };
  };
  /** Roads' default topsoil strip depth — feeds the Ponds/Swales row wording. */
  exTopsoilMm?: number;
}

export const buildStormBuildUpsMatrix = (
  input: StormBuildUpsInput,
  pricing: MatrixPricing,
): SectionMatrix => {
  // Row sets are config-driven exactly as StormSewersBillSummary.tsx builds them,
  // so a tender with 3 ponds and 2 Weholite tanks gets 3 pond rows and 2 tank rows.
  // Appended section-title text joins with " - " (withDashedTitleSuffix), as in the Bill Summaries.
  const pondsSwalesRows: StormBuildUpRowDef[] = withDashedTitleSuffix([
    ...getPondsRows(
      input.pondsSwales?.config?.pondSectionCount ?? DEFAULT_POND_SECTIONS,
      input.exTopsoilMm,
      input.pondsSwales?.config?.pondTopsoilThicknessMm ?? {},
      input.pondsSwales?.config?.sectionTitles,
    ),
    ...getSwalesRows(
      input.pondsSwales?.config?.swaleSectionCount ?? DEFAULT_SWALE_SECTIONS,
      input.exTopsoilMm,
      input.pondsSwales?.config?.swaleTopsoilThicknessMm ?? {},
      input.pondsSwales?.config?.sectionTitles,
    ),
    ...HEADWALL_ROWS,
  ]);
  const resolvePondsSwales = (row: BuildUpRowLike): string | undefined =>
    row.id.startsWith('hw_')
      ? findHeadwallEvaluateCode(row.description)
      : findPondsSwalesEvaluateCode(row.description);

  const cellularRows = withDashedTitleSuffix(getCellularAttenuationRows(
    input.cellular?.config?.sectionCount ?? DEFAULT_CELLULAR_SECTIONS,
    undefined,
    input.cellular?.config?.crateDims,
    input.cellular?.config?.sectionTitles,
  ));
  const weholiteSupplier = input.weholite?.config?.supplierName ?? 'Weholite';
  const weholiteRows = withDashedTitleSuffix(getWeholiteAttenuationRows(
    input.weholite?.config?.sectionCount ?? DEFAULT_WEHOLITE_SECTIONS,
    input.weholite?.config?.weholiteConfigPct ?? {},
    weholiteSupplier,
    input.weholite?.config?.sectionTitles,
  ));
  const concSupplier = input.conc?.config?.supplierName ?? 'Carlow';
  const concRows = withDashedTitleSuffix(getConcAttenuationRows(
    input.conc?.config?.sectionCount ?? DEFAULT_CONC_SECTIONS,
    input.conc?.config?.concConfigPct ?? {},
    concSupplier,
    input.conc?.config?.sectionTitles,
    input.conc?.config?.drawingRefs,
  ));
  const boxCulvertRows = getBoxCulvertsRows(
    input.boxCulverts?.config?.sectionCount ?? DEFAULT_BOX_CULVERT_SECTIONS,
    input.boxCulverts?.config?.culvertConfigMm ?? {},
    input.boxCulverts?.config?.culvertBackfillType ?? {},
  );

  const domain = (
    band: string,
    rows: StormBuildUpRowDef[],
    table: Record<string, FoulBuildUpRowData> | undefined,
    resolve: (row: BuildUpRowLike) => string | undefined,
    generalLabel: string,
  ): BuildUpDomainSpec => {
    // Built once — the exact tree StormBuildUpBillSummary.tsx renders for this
    // domain's tab — then reused for both this matrix's own rows/cells and its
    // billTotal, so the two can never independently diverge.
    const tree = buildStormBuildUpSummaryData(rows, table, (row) => resolve(row) ?? '');
    return { band, tree, generalLabel, billTotal: sumBuildUpTree(tree, pricing) };
  };

  return buildBuildUpMatrix(
    'storm-build-ups',
    'Ponds & Attenuation',
    'Element',
    [
      domain('PONDS & SWALES', pondsSwalesRows, input.pondsSwales?.table, resolvePondsSwales, 'Ponds & swales'),
      domain(
        'CELLULAR ATTENUATION',
        cellularRows,
        input.cellular?.table,
        (row) => findCellularAttenuationEvaluateCode(row.description),
        'Cellular attenuation',
      ),
      domain(
        `${weholiteSupplier.toUpperCase()} ATTENUATION`,
        weholiteRows,
        input.weholite?.table,
        (row) => findWeholiteAttenuationEvaluateCode(row.id),
        `${weholiteSupplier} attenuation`,
      ),
      domain(
        'CONC ATTENUATION',
        concRows,
        input.conc?.table,
        (row) => resolveConcAttenuationEvaluateCode(row),
        'Conc attenuation',
      ),
      domain(
        'BOX CULVERTS',
        boxCulvertRows,
        input.boxCulverts?.table,
        (row) => findBoxCulvertsEvaluateCode(row.description),
        'Box culverts',
      ),
    ],
    pricing,
    {
      note:
        'The Storm Sewers build-ups: one row per pond, swale, headwall, tank or culvert, with items grouped into work categories by description. Reconciles against the Ponds & Swales, Cellular, Weholite, Conc and Box Culverts tabs of the Storm Sewers Bill Summary. Rows whose quantity is a free-text spec note rather than a number carry no cost, same as the bill.',
      emptyHint:
        'No ponds, attenuation or culvert quantities entered yet. Enter them on the Storm Sewers page.',
    },
  );
};

// ── Entry point ──────────────────────────────────────────────────────────────

export interface SiteWorksMatrixInput {
  prelimsItems?: PrelimsItemRecord[];
  roads?: RoadsData;
  mainServicesTable?: Record<string, Record<string, number>>;
  substationTable?: Record<string, FoulBuildUpRowData>;
  pos?: {
    table?: Record<string, FoulBuildUpRowData>;
    config?: { sectionCount?: number; rowThicknessMm?: Record<string, number>; rowText?: Record<string, string> };
  };
  compound?: { table?: Record<string, FoulBuildUpRowData>; config?: CompoundConfig | Record<string, never> };
  spoil: SpoilReconciliationSource;
  /** Foul Sewers build-ups (sub-tab under Drainage Runs → Foul Sewers). */
  pumpStation?: { table?: Record<string, FoulBuildUpRowData>; config?: PumpChamberConfig | Record<string, never> };
  /** Foul Manhole Schedule's Ladders setting (material wording of Pump Station ladder rows). */
  foulLadders?: string;
  risingMain?: { table?: Record<string, FoulBuildUpRowData> };
  /** Storm Sewers build-ups (sub-tab under Drainage Runs → Storm Sewers). */
  stormBuildUps?: StormBuildUpsInput;
  pricing: MatrixPricing;
}

export interface SiteWorksMatrices {
  /** Top-level section-switcher matrices, in display order. */
  sections: SectionMatrix[];
  /** Sub-tab under Drainage Runs → Foul Sewers. */
  foulBuildUps: SectionMatrix;
  /** Sub-tab under Drainage Runs → Storm Sewers. */
  stormBuildUps: SectionMatrix;
}

/**
 * Builds every non-drainage matrix in one pass.
 *
 * OH&P / Preliminaries markup now applies to every fixed section's own rates
 * (Preliminaries, Roads, Main Services, Substation, POS, Compound, and — via
 * `buildRunMatrix` — Foul/Storm), matching each domain's own Bill Summary tab
 * and `useRoadsAndSewersTotalSync`'s per-domain totals — all of which apply
 * the same `roadsAndSewersMarkupFactor` to every domain, not just
 * Preliminaries (2026-09-15). `input.pricing` (the live OH&P/Preliminaries
 * factor) is passed straight through to every builder so the matrix can never
 * disagree with Sum Analysis. (Before 2026-09-15, only Preliminaries was ever
 * marked up anywhere in the app; every other builder here got a forced-net
 * `markupFactor: 1` for that reason, which no longer applies now that Sum
 * Analysis marks up every domain.)
 */
export const buildSiteWorksMatrices = (input: SiteWorksMatrixInput): SiteWorksMatrices => {
  return {
    sections: [
      buildPrelimsMatrix(input.prelimsItems, input.pricing),
      buildRoadsMatrix(input.roads, input.pricing),
      buildMainServicesMatrix(input.mainServicesTable, input.roads, input.pricing),
      buildSubstationMatrix(input.substationTable, input.pricing),
      buildPosMatrix(input.pos, input.pricing),
      buildCompoundMatrix(input.compound, input.pricing),
      buildSpoilMatrix(input.spoil),
    ],
    foulBuildUps: buildFoulBuildUpsMatrix(input.pumpStation, input.risingMain, input.pricing, input.foulLadders),
    stormBuildUps: buildStormBuildUpsMatrix(input.stormBuildUps ?? {}, input.pricing),
  };
};
